import { GeneratedPostsData, InstagramCarouselSlide } from "../types";
import { INSTAGRAM_CAROUSEL_MAX_SLIDES } from "./instagramCarouselLimits";

export function mediaItemToCarouselSlide(item: {
  cloudfront_url?: string;
  media_type: string;
}): InstagramCarouselSlide | null {
  const url = item.cloudfront_url?.trim() ?? "";
  if (!url) return null;
  return {
    url,
    type: item.media_type.toLowerCase() === "video" ? "VIDEO" : "IMAGE",
  };
}

/** Maps a PDF MediaItem to a LinkedIn document carousel slide. */
export function mediaToPdfSlide(item: {
  cloudfront_url?: string;
}): InstagramCarouselSlide | null {
  const url = item.cloudfront_url?.trim() ?? "";
  if (!url) return null;
  return { url, type: "PDF" };
}

export function patchGeneratedPostsByTempId(
  data: GeneratedPostsData | null | undefined,
  tempId: string,
  imageUrl: string
): GeneratedPostsData | null {
  if (!data?.posts?.length) return data ?? null;
  if (!data.posts.some((p) => p.temp_id === tempId)) return data;

  return {
    posts: data.posts.map((p) =>
      p.temp_id === tempId
        ? {
            ...p,
            image_url: imageUrl,
            instagram_content_format: "image",
            video_url: "",
            carousel_slides: undefined,
          }
        : p
    ),
  };
}

export function patchGeneratedPostsByIndex(
  data: GeneratedPostsData | null | undefined,
  index: number,
  imageUrl: string
): GeneratedPostsData | null | undefined {
  if (!data?.posts?.length) return data;
  return {
    posts: data.posts.map((p, i) => (i === index ? { ...p, image_url: imageUrl } : p)),
  };
}

/** Set carousel slides from My Library Done (full replace). */
export function patchGeneratedPostsSetCarouselSlides(
  data: GeneratedPostsData | null | undefined,
  tempId: string,
  slides: InstagramCarouselSlide[]
): GeneratedPostsData | null {
  if (!data?.posts?.length) return data ?? null;
  if (!data.posts.some((p) => p.temp_id === tempId)) return data;

  const capped = slides.slice(0, INSTAGRAM_CAROUSEL_MAX_SLIDES);
  return {
    posts: data.posts.map((p) => {
      if (p.temp_id !== tempId) return p;
      return {
        ...p,
        instagram_content_format: "carousel",
        carousel_slides: capped,
        image_url: capped[0]?.url ?? "",
        video_url: "",
      };
    }),
  };
}

/** Append slides to existing carousel (from "+" add-slide flow). */
export function patchGeneratedPostsAppendCarouselSlides(
  data: GeneratedPostsData | null | undefined,
  tempId: string,
  newSlides: InstagramCarouselSlide[]
): GeneratedPostsData | null {
  if (!data?.posts?.length) return data ?? null;
  if (!data.posts.some((p) => p.temp_id === tempId)) return data;

  return {
    posts: data.posts.map((p) => {
      if (p.temp_id !== tempId) return p;
      const existing = p.carousel_slides ?? [];
      const merged = [...existing, ...newSlides].slice(0, INSTAGRAM_CAROUSEL_MAX_SLIDES);
      return {
        ...p,
        instagram_content_format: "carousel",
        carousel_slides: merged,
        image_url: merged[0]?.url ?? "",
        video_url: "",
      };
    }),
  };
}

/**
 * LinkedIn multi-PDF: replace carousel_slides with PDF slides from My Library Done.
 * Does NOT touch instagram_content_format (irrelevant for LinkedIn).
 */
export function patchGeneratedPostsSetPdfSlides(
  data: GeneratedPostsData | null | undefined,
  tempId: string,
  slides: InstagramCarouselSlide[]
): GeneratedPostsData | null {
  if (!data?.posts?.length) return data ?? null;
  if (!data.posts.some((p) => p.temp_id === tempId)) return data;

  const capped = slides.slice(0, INSTAGRAM_CAROUSEL_MAX_SLIDES);
  return {
    posts: data.posts.map((p) => {
      if (p.temp_id !== tempId) return p;
      return {
        ...p,
        carousel_slides: capped,
        document_url: "",
        image_url: "",
        video_url: "",
      };
    }),
  };
}

/** Patch platform_post_id / business_profile_id / platform_response back onto a card after publish. */
export function patchGeneratedPostPublishResult(
  data: GeneratedPostsData | null | undefined,
  tempId: string,
  result: { platform_post_id?: string; business_profile_id?: string; platform_response?: any }
): GeneratedPostsData | null {
  if (!data?.posts?.length) return data ?? null;
  if (!data.posts.some((p) => p.temp_id === tempId)) return data;
  return {
    posts: data.posts.map((p) =>
      p.temp_id === tempId ? { ...p, ...result } : p
    ),
  };
}
